import React, { useState, useEffect } from "react";
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  FormGroup,
  Label,
  Input,
  Row,
  Col,
  Nav,
  NavItem,
  NavLink,
  TabContent,
  TabPane,
} from "reactstrap";
import { toast } from "react-toastify";
import {
  Monitor,
  Edit2,
  Check,
  X,
  Plus,
  Trash2,
  UserPlus,
} from "react-feather";
import { pointService } from "../../../services/pointService";
import { pointTypeService } from "../../../services/pointTypeService";
import { deviceService } from "../../../services/deviceService";
import { userService } from "../../../services/userService";
import ConfirmDeleteDialog from "../../Common/ConfirmDeleteDialog";
import AssignPointToDeviceModal from "./AssignPointToDeviceModal";
import EditPointModal from "./EditPointModal";

/**
 * DeviceDetailsModal - Full modal for viewing/editing device details, points, and users.
 * Manages its own local state for editing/points/users.
 * Props:
 *   isOpen: bool
 *   device: the device to view (viewingDevice)
 *   onClose: () => void
 *   allRooms: array of all rooms (for room selection in edit mode)
 *   deviceTypes: array of device types
 *   loadingDeviceTypes: bool
 *   onFetchDeviceTypes: () => void — called when device types need to be loaded
 *   onSuccess: () => void — called after updates to refresh parent site details
 */
const DeviceDetailsModal = ({
  isOpen,
  device,
  onClose,
  allRooms,
  deviceTypes,
  loadingDeviceTypes,
  onFetchDeviceTypes,
  onSuccess,
}) => {
  const [editingDevice, setEditingDevice] = useState(false);
  const [editDeviceData, setEditDeviceData] = useState({
    name: "",
    description: "",
    deviceTypeId: "",
    roomId: "",
  });
  const [updatingDevice, setUpdatingDevice] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deviceDetailsActiveTab, setDeviceDetailsActiveTab] = useState("details");

  // Points state
  const [devicePoints, setDevicePoints] = useState([]);
  const [loadingDevicePoints, setLoadingDevicePoints] = useState(false);
  const [assigningPointToDevice, setAssigningPointToDevice] = useState(false);
  const [editingPoint, setEditingPoint] = useState(null);
  const [removingPointFromDevice, setRemovingPointFromDevice] = useState(null);
  const [removingPoint, setRemovingPoint] = useState(false);

  // Device user assignment state
  const [deviceUsers, setDeviceUsers] = useState([]);
  const [loadingDeviceUsers, setLoadingDeviceUsers] = useState(false);
  const [deviceUserSearchTerm, setDeviceUserSearchTerm] = useState("");
  const [selectedDeviceUserName, setSelectedDeviceUserName] = useState("");
  const [assigningDeviceUser, setAssigningDeviceUser] = useState(false);
  const [deviceUsersList, setDeviceUsersList] = useState([]);
  const [loadingDeviceUsersList, setLoadingDeviceUsersList] = useState(false);
  const [unassigningDeviceUser, setUnassigningDeviceUser] = useState(null);
  const [unassigningDeviceUserLoading, setUnassigningDeviceUserLoading] = useState(false);
  const [pointTypes, setPointTypes] = useState([]);
  const [loadingPointTypes, setLoadingPointTypes] = useState(false);

  useEffect(() => {
    if (isOpen && device) {
      setEditingDevice(false);
      setDeviceDetailsActiveTab("details");
      setEditDeviceData({
        name: device.name || "",
        description: device.description || "",
        deviceTypeId: device.deviceTypeId || "",
        roomId: device.roomId || "",
      });
      setDeviceUserSearchTerm("");
      setSelectedDeviceUserName("");
      fetchDevicePoints(device.id);
      fetchDeviceUsers(device.id);
      fetchDeviceUsersList();
      fetchPointTypes();
    } else if (!isOpen) {
      // Reset everything on close
      setEditingDevice(false);
      setConfirmingDelete(false);
      setDeleting(false);
      setDeviceDetailsActiveTab("details");
      setDevicePoints([]);
      setDeviceUsers([]);
      setDeviceUsersList([]);
      setSelectedDeviceUserName("");
      setDeviceUserSearchTerm("");
      setEditDeviceData({ name: "", description: "", deviceTypeId: "", roomId: "" });
      setAssigningPointToDevice(false);
      setEditingPoint(null);
      setRemovingPointFromDevice(null);
      setUnassigningDeviceUser(null);
    }
  }, [isOpen, device]);

  const fetchPointTypes = async () => {
    setLoadingPointTypes(true);
    try {
      const response = await pointTypeService.search({ name: "", page: 0, size: 1000 });
      setPointTypes(response.content || []);
    } catch (error) {
      console.error("Error fetching point types:", error);
      toast.error("Failed to load point types");
    } finally {
      setLoadingPointTypes(false);
    }
  };

  const fetchDevicePoints = async (deviceId) => {
    setLoadingDevicePoints(true);
    try {
      const response = await pointService.getByDevice(deviceId);
      setDevicePoints(response || []);
    } catch (error) {
      console.error("Error fetching device points:", error);
      toast.error("Failed to load device points");
      setDevicePoints([]);
    } finally {
      setLoadingDevicePoints(false);
    }
  };

  const fetchDeviceUsers = async (deviceId) => {
    setLoadingDeviceUsers(true);
    try {
      const response = await deviceService.getEffectiveUserMappings(deviceId);
      setDeviceUsers(response || []);
    } catch (error) {
      console.error("Error fetching device users:", error);
      toast.error("Failed to load device users");
      setDeviceUsers([]);
    } finally {
      setLoadingDeviceUsers(false);
    }
  };

  const fetchDeviceUsersList = async (searchName = "") => {
    setLoadingDeviceUsersList(true);
    try {
      const params = {
        userType: "USER",
        ...(searchName?.trim() && { name: searchName.trim() }),
      };
      const response = await userService.list(params);
      setDeviceUsersList(response.content || []);
    } catch (error) {
      console.error("Error fetching users for device:", error);
      toast.error("Failed to load users");
    } finally {
      setLoadingDeviceUsersList(false);
    }
  };

  const handleEditDevice = () => {
    setEditingDevice(true);
    if (deviceTypes.length === 0) {
      onFetchDeviceTypes();
    }
  };

  const handleCancelEditDevice = () => {
    setEditingDevice(false);
    setEditDeviceData({
      name: device.name || "",
      description: device.description || "",
      deviceTypeId: device.deviceTypeId || "",
      roomId: device.roomId || "",
    });
  };

  const handleDeleteDevice = async () => {
    if (!device) return;
    setDeleting(true);
    try {
      await deviceService.remove(device.id);
      toast.success("Device deleted successfully");
      if (onSuccess) onSuccess();
      onClose();
    } catch (error) {
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error("Failed to delete device");
      }
    } finally {
      setDeleting(false);
      setConfirmingDelete(false);
    }
  };

  const handleUpdateDevice = async () => {
    if (!editDeviceData.name.trim()) {
      toast.error("Device name is required");
      return;
    }
    if (!editDeviceData.deviceTypeId) {
      toast.error("Device type is required");
      return;
    }
    if (!editDeviceData.roomId) {
      toast.error("Room is required");
      return;
    }

    setUpdatingDevice(true);
    try {
      const updateData = {
        name: editDeviceData.name.trim(),
        description: editDeviceData.description.trim(),
        deviceTypeId: parseInt(editDeviceData.deviceTypeId),
        roomId: parseInt(editDeviceData.roomId),
      };

      await deviceService.update(device.id, updateData);
      toast.success("Device updated successfully");

      if (onSuccess) onSuccess();
      onClose();
    } catch (error) {
      console.error("Error updating device:", error);
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error("Failed to update device");
      }
    } finally {
      setUpdatingDevice(false);
    }
  };

  const handleDeviceUserSearch = (searchTerm) => {
    setDeviceUserSearchTerm(searchTerm);
    fetchDeviceUsersList(searchTerm);
  };

  const handleAssignDeviceUser = async () => {
    if (!selectedDeviceUserName || !device) {
      toast.error("Please select a user");
      return;
    }

    setAssigningDeviceUser(true);
    try {
      await deviceService.assignUser({ userName: selectedDeviceUserName, deviceId: device.id });
      toast.success("User assigned to device successfully");

      await fetchDeviceUsers(device.id);
      setSelectedDeviceUserName("");
      setDeviceUserSearchTerm("");
      fetchDeviceUsersList();
    } catch (error) {
      console.error("Error assigning user to device:", error);
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error("Failed to assign user to device");
      }
    } finally {
      setAssigningDeviceUser(false);
    }
  };

  const handleUnassignDeviceUser = (user) => {
    if (user.grantLevel !== "DEVICE") return; // inherited grants cannot be removed here
    setUnassigningDeviceUser(user);
  };

  const handleCancelUnassignDeviceUser = () => {
    setUnassigningDeviceUser(null);
  };

  const handleConfirmUnassignDeviceUser = async () => {
    if (!unassigningDeviceUser || !device) return;

    setUnassigningDeviceUserLoading(true);
    try {
      await deviceService.unassignUser({ userName: unassigningDeviceUser.userName, deviceId: device.id });
      toast.success("User unassigned from device successfully");

      await fetchDeviceUsers(device.id);
      fetchDeviceUsersList(deviceUserSearchTerm);
      setUnassigningDeviceUser(null);
    } catch (error) {
      console.error("Error unassigning user from device:", error);
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error("Failed to unassign user from device");
      }
    } finally {
      setUnassigningDeviceUserLoading(false);
    }
  };

  const handleRemovePointFromDevice = (point) => {
    setRemovingPointFromDevice(point);
  };

  const handleCancelRemovePoint = () => {
    setRemovingPointFromDevice(null);
  };

  const handleConfirmRemovePoint = async () => {
    if (!removingPointFromDevice || !device) return;

    setRemovingPoint(true);
    try {
      await pointService.unassignDevice(removingPointFromDevice.pointId);
      toast.success("Point removed from device successfully");

      await fetchDevicePoints(device.id);
      if (onSuccess) onSuccess();

      setRemovingPointFromDevice(null);
    } catch (error) {
      console.error("Error removing point from device:", error);
      if (error.response?.data?.message) {
        toast.error(error.response.data.message);
      } else {
        toast.error("Failed to remove point from device");
      }
    } finally {
      setRemovingPoint(false);
    }
  };

  const handlePointSuccess = async () => {
    if (device) {
      await fetchDevicePoints(device.id);
    }
    if (onSuccess) onSuccess();
  };

  return (
    <>
      <Modal isOpen={isOpen && !confirmingDelete} toggle={onClose} size="lg">
        <ModalHeader toggle={onClose}>
          {editingDevice ? "Edit Device" : "Device Details"}
        </ModalHeader>
        <ModalBody>
          {device && (
            <>
              {editingDevice ? (
                <>
                  <h6 className="mb-3 d-flex align-items-center">
                    <Monitor size={20} className="me-2 text-success" />
                    Device Information
                  </h6>
                  <Row>
                    <Col md="6">
                      <FormGroup>
                        <Label>ID</Label>
                        <Input type="text" value={device.id || "-"} disabled />
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup>
                        <Label>
                          Name <span className="text-danger">*</span>
                        </Label>
                        <Input
                          type="text"
                          value={editDeviceData.name}
                          onChange={(e) =>
                            setEditDeviceData({ ...editDeviceData, name: e.target.value })
                          }
                          disabled={updatingDevice}
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row>
                    <Col md="12">
                      <FormGroup>
                        <Label>Description</Label>
                        <Input
                          type="textarea"
                          value={editDeviceData.description}
                          onChange={(e) =>
                            setEditDeviceData({ ...editDeviceData, description: e.target.value })
                          }
                          disabled={updatingDevice}
                          rows="2"
                        />
                      </FormGroup>
                    </Col>
                  </Row>
                  <Row>
                    <Col md="6">
                      <FormGroup>
                        <Label>
                          Device Type <span className="text-danger">*</span>
                        </Label>
                        <Input
                          type="select"
                          value={editDeviceData.deviceTypeId}
                          onChange={(e) =>
                            setEditDeviceData({ ...editDeviceData, deviceTypeId: e.target.value })
                          }
                          disabled={updatingDevice || loadingDeviceTypes}
                        >
                          <option value="">Select device type...</option>
                          {deviceTypes.map((type) => (
                            <option key={type.id} value={type.id}>
                              {type.name} - {type.description || "No description"}
                            </option>
                          ))}
                        </Input>
                        {loadingDeviceTypes && (
                          <small className="text-muted">Loading device types...</small>
                        )}
                      </FormGroup>
                    </Col>
                    <Col md="6">
                      <FormGroup>
                        <Label>
                          Room <span className="text-danger">*</span>
                        </Label>
                        <Input
                          type="select"
                          value={editDeviceData.roomId}
                          onChange={(e) =>
                            setEditDeviceData({ ...editDeviceData, roomId: e.target.value })
                          }
                          disabled={updatingDevice}
                        >
                          <option value="">Select room...</option>
                          {(allRooms || []).map((room) => (
                            <option key={room.id} value={room.id}>
                              {room.name} (ID: {room.id})
                            </option>
                          ))}
                        </Input>
                      </FormGroup>
                    </Col>
                  </Row>
                </>
              ) : (
                <>
                  {/* Tabs for Device Details, Points, Users */}
                  <Nav tabs>
                    <NavItem>
                      <NavLink
                        className={deviceDetailsActiveTab === "details" ? "active" : ""}
                        onClick={() => setDeviceDetailsActiveTab("details")}
                        style={{ cursor: "pointer" }}
                      >
                        Device Details
                      </NavLink>
                    </NavItem>
                    <NavItem>
                      <NavLink
                        className={deviceDetailsActiveTab === "points" ? "active" : ""}
                        onClick={() => setDeviceDetailsActiveTab("points")}
                        style={{ cursor: "pointer" }}
                      >
                        Points ({devicePoints.length})
                      </NavLink>
                    </NavItem>
                    <NavItem>
                      <NavLink
                        className={deviceDetailsActiveTab === "users" ? "active" : ""}
                        onClick={() => setDeviceDetailsActiveTab("users")}
                        style={{ cursor: "pointer" }}
                      >
                        Users ({deviceUsers.length})
                      </NavLink>
                    </NavItem>
                  </Nav>

                  <TabContent activeTab={deviceDetailsActiveTab} className="mt-3">
                    {/* Details Tab */}
                    <TabPane tabId="details">
                      <h6 className="mb-3 d-flex align-items-center">
                        <Monitor size={20} className="me-2 text-success" />
                        Device Information
                      </h6>
                      <Row>
                        <Col md="6">
                          <FormGroup>
                            <Label>ID</Label>
                            <Input type="text" value={device.id || "-"} disabled />
                          </FormGroup>
                        </Col>
                        <Col md="6">
                          <FormGroup>
                            <Label>Name</Label>
                            <Input type="text" value={device.name || "-"} disabled />
                          </FormGroup>
                        </Col>
                      </Row>
                      <Row>
                        <Col md="12">
                          <FormGroup>
                            <Label>Description</Label>
                            <Input
                              type="textarea"
                              value={device.description || "-"}
                              disabled
                              rows="2"
                            />
                          </FormGroup>
                        </Col>
                      </Row>
                      <Row>
                        <Col md="6">
                          <FormGroup>
                            <Label>Device Type</Label>
                            <Input type="text" value={device.deviceTypeName || "-"} disabled />
                          </FormGroup>
                        </Col>
                        <Col md="6">
                          <FormGroup>
                            <Label>Room</Label>
                            <Input type="text" value={device.roomName || "-"} disabled />
                          </FormGroup>
                        </Col>
                      </Row>
                      <Row>
                        <Col md="12">
                          <FormGroup>
                            <Label>Partner UUID</Label>
                            <Input type="text" value={device.partnerUuid || "-"} disabled />
                          </FormGroup>
                        </Col>
                      </Row>
                      <Row>
                        <Col md="6">
                          <FormGroup>
                            <Label>Assigned By</Label>
                            <Input type="text" value={device.assignedBy || "-"} disabled />
                          </FormGroup>
                        </Col>
                        <Col md="6">
                          <FormGroup>
                            <Label>Assigned At</Label>
                            <Input
                              type="text"
                              value={
                                device.assignedAt
                                  ? new Date(device.assignedAt).toLocaleString()
                                  : "-"
                              }
                              disabled
                            />
                          </FormGroup>
                        </Col>
                      </Row>
                      <Row>
                        <Col md="6">
                          <FormGroup>
                            <Label>Created At</Label>
                            <Input
                              type="text"
                              value={new Date(device.createdAt).toLocaleString()}
                              disabled
                            />
                          </FormGroup>
                        </Col>
                        <Col md="6">
                          <FormGroup>
                            <Label>Updated At</Label>
                            <Input
                              type="text"
                              value={new Date(device.updatedAt).toLocaleString()}
                              disabled
                            />
                          </FormGroup>
                        </Col>
                      </Row>
                    </TabPane>

                    {/* Points Tab */}
                    <TabPane tabId="points">
                      <div className="d-flex justify-content-between align-items-center mb-3">
                        <h6 className="mb-0">Device Points</h6>
                        <Button
                          color="primary"
                          size="sm"
                          onClick={() => setAssigningPointToDevice(true)}
                        >
                          <Plus size={14} className="me-1" />
                          Add Point
                        </Button>
                      </div>

                      {loadingDevicePoints ? (
                        <div className="text-center p-4">
                          <div className="spinner-border" role="status">
                            <span className="visually-hidden">Loading...</span>
                          </div>
                          <div className="mt-2 text-muted">Loading device points...</div>
                        </div>
                      ) : devicePoints.length === 0 ? (
                        <div className="text-center text-muted p-4">
                          <Monitor size={48} style={{ opacity: 0.3 }} className="mb-3" />
                          <p className="mb-0">No points found for this device.</p>
                          <p className="small">Click "Add Point" to assign points to this device.</p>
                        </div>
                      ) : (
                        <div
                          className="table-responsive"
                          style={{ maxHeight: "400px", overflowY: "auto" }}
                        >
                          <table className="table table-sm table-hover">
                            <thead
                              className="table-light"
                              style={{ position: "sticky", top: 0 }}
                            >
                              <tr>
                                <th style={{ fontSize: "0.85rem" }}>Display Name</th>
                                <th style={{ fontSize: "0.85rem" }}>Point Type</th>
                                <th style={{ fontSize: "0.85rem" }}>Status</th>
                                <th style={{ fontSize: "0.85rem", textAlign: "center" }} title="Main Read Point">Main read</th>
                                <th style={{ fontSize: "0.85rem", textAlign: "center" }} title="Main Write Point">Main write</th>
                                <th style={{ fontSize: "0.85rem", textAlign: "center" }}>Order</th>
                                <th style={{ fontSize: "0.85rem", textAlign: "center" }}>Action</th>
                              </tr>
                            </thead>
                            <tbody>
                              {devicePoints.map((point, index) => (
                                <tr key={index}>
                                  <td style={{ fontSize: "0.85rem", fontWeight: "500" }}>
                                    {point.displayName || "-"}
                                  </td>
                                  <td style={{ fontSize: "0.85rem" }}>{point.pointTypeEnum || "-"}</td>
                                  <td style={{ fontSize: "0.85rem" }}>
                                    <span
                                      className={`badge ${
                                        point.curStatus === "ok"
                                          ? "bg-success"
                                          : point.curStatus === "stale"
                                          ? "bg-warning"
                                          : "bg-secondary"
                                      }`}
                                    >
                                      {point.curStatus || "-"}
                                    </span>
                                  </td>
                                  <td style={{ fontSize: "0.85rem", textAlign: "center" }}>
                                    {point.isMainReadPoint ? (
                                      <Check size={16} className="text-success" />
                                    ) : (
                                      <span className="text-muted">-</span>
                                    )}
                                  </td>
                                  <td style={{ fontSize: "0.85rem", textAlign: "center" }}>
                                    {point.isMainWritePoint ? (
                                      <Check size={16} className="text-success" />
                                    ) : (
                                      <span className="text-muted">-</span>
                                    )}
                                  </td>
                                  <td style={{ fontSize: "0.85rem", textAlign: "center" }}>
                                    {point.displayOrder !== null && point.displayOrder !== undefined
                                      ? point.displayOrder
                                      : <span className="text-muted">-</span>}
                                  </td>
                                  <td style={{ textAlign: "center" }}>
                                    <div className="d-flex gap-1 justify-content-center">
                                      <Button
                                        color="primary"
                                        size="sm"
                                        outline
                                        onClick={() => setEditingPoint(point)}
                                        title="Edit point settings"
                                        style={{ padding: "2px 8px", fontSize: "0.75rem" }}
                                      >
                                        <Edit2 size={12} className="me-1" />
                                        Edit
                                      </Button>
                                      <Button
                                        color="danger"
                                        size="sm"
                                        outline
                                        onClick={() => handleRemovePointFromDevice(point)}
                                        title="Remove point from device"
                                        style={{ padding: "2px 8px", fontSize: "0.75rem" }}
                                      >
                                        <Trash2 size={12} className="me-1" />
                                        Remove
                                      </Button>
                                    </div>
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </TabPane>

                    {/* Users Tab */}
                    <TabPane tabId="users">
                      {/* ── Assign section ─────────────────────────────── */}
                      <div className="mb-3">
                        <h6 className="mb-1 d-flex align-items-center">
                          <UserPlus size={16} className="me-2 text-warning" />
                          Assign direct device access
                        </h6>
                        <small className="text-muted d-block mb-3">
                          Only users without any existing access appear below.
                        </small>

                        <FormGroup>
                          <Input
                            type="text"
                            placeholder="Search users by name..."
                            value={deviceUserSearchTerm}
                            onChange={(e) => handleDeviceUserSearch(e.target.value)}
                            disabled={assigningDeviceUser}
                          />
                        </FormGroup>

                        <FormGroup>
                          <Input
                            type="select"
                            value={selectedDeviceUserName}
                            onChange={(e) => setSelectedDeviceUserName(e.target.value)}
                            disabled={assigningDeviceUser || loadingDeviceUsersList}
                            size="5"
                          >
                            <option value="">Select a user...</option>
                            {deviceUsersList
                              .filter(
                                (user) =>
                                  !deviceUsers.some((d) => d.userName === user.userName)
                              )
                              .map((user) => (
                                <option key={user.id} value={user.userName}>
                                  {user.userName} - {user.email}
                                  {!user.active && " (Inactive)"}
                                  {!user.verified && " (Unverified)"}
                                </option>
                              ))}
                          </Input>
                          {loadingDeviceUsersList && (
                            <small className="text-muted mt-1 d-block">
                              <span className="spinner-border spinner-border-sm me-1"></span>
                              Loading users...
                            </small>
                          )}
                        </FormGroup>

                        <div className="d-flex justify-content-end">
                          <Button
                            color="warning"
                            size="sm"
                            onClick={handleAssignDeviceUser}
                            disabled={assigningDeviceUser || !selectedDeviceUserName}
                          >
                            {assigningDeviceUser ? (
                              <>
                                <span className="spinner-border spinner-border-sm me-2"></span>
                                Assigning...
                              </>
                            ) : (
                              <>
                                <UserPlus size={14} className="me-1" />
                                Assign to Device
                              </>
                            )}
                          </Button>
                        </div>
                      </div>

                      <hr />

                      {/* ── Effective access list ───────────────────────── */}
                      <div className="d-flex align-items-center justify-content-between mb-2">
                        <h6 className="mb-0">Who has access ({deviceUsers.length})</h6>
                        <div className="d-flex gap-2">
                          <span className="badge bg-success" style={{ fontSize: "0.7rem" }}>DEVICE</span>
                          <span className="badge bg-warning text-dark" style={{ fontSize: "0.7rem" }}>ROOM</span>
                          <span className="badge bg-primary" style={{ fontSize: "0.7rem" }}>SITE</span>
                        </div>
                      </div>

                      {loadingDeviceUsers ? (
                        <div className="text-center p-3">
                          <div className="spinner-border spinner-border-sm" role="status" />
                          <div className="mt-2 text-muted small">Loading...</div>
                        </div>
                      ) : deviceUsers.length === 0 ? (
                        <div className="text-center text-muted p-3">
                          <UserPlus size={36} style={{ opacity: 0.3 }} className="mb-2" />
                          <p className="mb-0 small">No users have access to this device yet.</p>
                        </div>
                      ) : (
                        <div className="table-responsive" style={{ maxHeight: "280px", overflowY: "auto" }}>
                          <table className="table table-sm table-hover mb-0">
                            <thead className="table-light" style={{ position: "sticky", top: 0 }}>
                              <tr>
                                <th style={{ fontSize: "0.8rem" }}>Username</th>
                                <th style={{ fontSize: "0.8rem" }}>Access via</th>
                                <th style={{ fontSize: "0.8rem" }}>Granted by</th>
                                <th style={{ fontSize: "0.8rem" }}>Granted at</th>
                                <th style={{ fontSize: "0.8rem", textAlign: "center" }}>Action</th>
                              </tr>
                            </thead>
                            <tbody>
                              {deviceUsers.map((user, idx) => {
                                const isDirect = user.grantLevel === "DEVICE";
                                const badgeColor =
                                  user.grantLevel === "DEVICE" ? "success"
                                  : user.grantLevel === "ROOM"   ? "warning"
                                  : "primary";
                                const badgeTextClass = user.grantLevel === "ROOM" ? "text-dark" : "";
                                return (
                                  <tr key={idx} style={isDirect ? {} : { backgroundColor: "rgba(0,0,0,0.02)" }}>
                                    <td style={{ fontSize: "0.82rem", fontWeight: "500", verticalAlign: "middle" }}>
                                      {user.userName || "-"}
                                    </td>
                                    <td style={{ fontSize: "0.82rem", verticalAlign: "middle" }}>
                                      <span className={`badge bg-${badgeColor} ${badgeTextClass} me-1`} style={{ fontSize: "0.68rem" }}>
                                        {user.grantLevel}
                                      </span>
                                      {user.via && (
                                        <small className="text-muted">{user.via}</small>
                                      )}
                                    </td>
                                    <td style={{ fontSize: "0.82rem", verticalAlign: "middle" }}>
                                      {user.grantedBy || "-"}
                                    </td>
                                    <td style={{ fontSize: "0.82rem", verticalAlign: "middle" }}>
                                      {user.grantedAt
                                        ? new Date(user.grantedAt).toLocaleString()
                                        : "-"}
                                    </td>
                                    <td style={{ textAlign: "center", verticalAlign: "middle" }}>
                                      {isDirect ? (
                                        <Button
                                          color="danger"
                                          size="sm"
                                          outline
                                          onClick={() => handleUnassignDeviceUser(user)}
                                          style={{ padding: "2px 8px", fontSize: "0.72rem" }}
                                        >
                                          <X size={11} className="me-1" />
                                          Unassign
                                        </Button>
                                      ) : (
                                        <small
                                          className="text-muted"
                                          title={`Inherited from ${user.grantLevel === "ROOM" ? "room" : "site"} — manage from the ${user.grantLevel === "ROOM" ? "Room" : "Site"} Users panel`}
                                          style={{ cursor: "help", textDecoration: "underline dotted" }}
                                        >
                                          inherited
                                        </small>
                                      )}
                                    </td>
                                  </tr>
                                );
                              })}
                            </tbody>
                          </table>
                        </div>
                      )}
                    </TabPane>
                  </TabContent>
                </>
              )}
            </>
          )}
        </ModalBody>
        <ModalFooter>
          {editingDevice ? (
            <>
              <Button color="secondary" onClick={handleCancelEditDevice} disabled={updatingDevice}>
                Cancel
              </Button>
              <Button
                color="primary"
                onClick={handleUpdateDevice}
                disabled={updatingDevice || loadingDeviceTypes}
              >
                {updatingDevice ? (
                  <>
                    <span className="spinner-border spinner-border-sm me-2"></span>
                    Updating...
                  </>
                ) : (
                  <>
                    <Check size={14} className="me-1" />
                    Update Device
                  </>
                )}
              </Button>
            </>
          ) : (
            <>
              <Button
                color="danger"
                outline
                onClick={() => setConfirmingDelete(true)}
                className="me-auto"
              >
                <Trash2 size={14} className="me-1" />
                Delete
              </Button>
              <Button color="primary" onClick={handleEditDevice}>
                <Edit2 size={14} className="me-1" />
                Edit
              </Button>
              <Button color="secondary" onClick={onClose}>
                Close
              </Button>
            </>
          )}
        </ModalFooter>
      </Modal>

      {/* Assign Point to Device Modal */}
      <AssignPointToDeviceModal
        isOpen={assigningPointToDevice}
        device={device}
        onClose={() => setAssigningPointToDevice(false)}
        onSuccess={handlePointSuccess}
      />

      {/* Edit Point Modal */}
      <EditPointModal
        isOpen={!!editingPoint}
        point={editingPoint}
        deviceId={device?.id}
        deviceName={device?.name}
        onClose={() => setEditingPoint(null)}
        onSuccess={handlePointSuccess}
        pointTypes={pointTypes}
        loadingPointTypes={loadingPointTypes}
      />

      {/* Remove Point Confirmation */}
      <ConfirmDeleteDialog
        isOpen={!!removingPointFromDevice}
        headerText="Confirm Remove"
        title="Remove Point"
        subtitle="This action will unassign the point"
        bodyText={
          <>
            Are you sure you want to remove point{" "}
            <strong>
              "{removingPointFromDevice?.pointDisplayName || removingPointFromDevice?.pointId}"
            </strong>{" "}
            from device <strong>"{device?.name}"</strong>?
          </>
        }
        warningText="The point will be unassigned from this device."
        warningLabel="Note"
        actionText="Remove Point"
        loadingText="Removing..."
        onClose={handleCancelRemovePoint}
        onConfirm={handleConfirmRemovePoint}
        loading={removingPoint}
      />

      {/* Unassign Device User Confirmation */}
      <ConfirmDeleteDialog
        isOpen={!!unassigningDeviceUser}
        headerText="Confirm Unassign"
        title="Unassign User from Device"
        subtitle="This action will remove user access"
        icon={X}
        bodyText={
          <>
            Are you sure you want to unassign user{" "}
            <strong>"{unassigningDeviceUser?.userName}"</strong> from device{" "}
            <strong>"{device?.name}"</strong>?
          </>
        }
        warningText="The user will no longer have access to this device and its points."
        warningLabel="Note"
        actionText="Unassign User"
        loadingText="Unassigning..."
        onClose={handleCancelUnassignDeviceUser}
        onConfirm={handleConfirmUnassignDeviceUser}
        loading={unassigningDeviceUserLoading}
      />

      <ConfirmDeleteDialog
        isOpen={confirmingDelete}
        onClose={() => setConfirmingDelete(false)}
        onConfirm={handleDeleteDevice}
        deleting={deleting}
        title="Delete Device"
        itemName={device?.name}
        warningText="All user assignments for this device will also be removed. The device must have no points assigned."
      />
    </>
  );
};

export default DeviceDetailsModal;
